import { Link } from 'react-router-dom';
import { useState } from 'react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Shield, CheckCircle, Building2, Package, TrendingUp, Award, Check, CalendarDays, MapPin, ShieldCheck, BadgeCheck, Factory, Truck, Boxes, ChevronDown, DollarSign, Globe, BarChart3, UserX } from 'lucide-react';

const painPoints = [
  {
    icon: DollarSign,
    question: "Maintaining Consistent Pricing Across Marketplaces",
    answer: "Multiple sellers and fragmented distribution can quickly erode pricing control. VERLINE supports brands with disciplined distribution and MAP-aligned marketplace operations."
  },
  {
    icon: Globe,
    question: "Scaling Across Multiple Marketplaces",
    answer: "Expanding across Amazon, Walmart, and other channels requires operational expertise and reliable marketplace infrastructure."
  },
  {
    icon: BarChart3,
    question: "Listings and Advertising That Don't Drive Growth",
    answer: "Our advertising specialists manage and optimize marketplace campaigns to increase visibility and support sustainable demand."
  },
  {
    icon: UserX,
    question: "Unauthorized Sellers Impacting Brand Value",
    answer: "VERLINE works with brands to support responsible marketplace distribution and protect brand integrity across online channels."
  }
];

const PainPointsSection = () => {
  const cards = [
    {
      icon: DollarSign,
      title: 'Pricing Control',
      description: 'Prevent price erosion with disciplined distribution and MAP-aligned marketplace operations.',
      testId: 'pillar-pricing',
    },
    {
      icon: BarChart3,
      title: 'Marketplace Growth',
      description: 'Expand visibility and sales through optimized listings, advertising, and inventory strategy.',
      testId: 'pillar-growth',
    },
    {
      icon: Shield,
      title: 'Brand Protection',
      description: 'Control distribution to prevent unauthorized sellers from damaging pricing and reputation.',
      testId: 'pillar-protection',
    },
    {
      icon: Package,
      title: 'Fulfillment Infrastructure',
      description: 'Scalable logistics and fulfillment systems built for consistent marketplace performance.',
      testId: 'pillar-fulfillment',
    },
  ];

  return (
    <section style={{ background: '#ffffff', padding: '80px 0 88px', position: 'relative' }} data-testid="pain-points-section">
      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 24px', position: 'relative' }}>
        <div className="text-center" style={{ marginBottom: '48px' }}>
          <h2 className="font-bold" style={{ fontFamily: "'General Sans', sans-serif", fontSize: 'clamp(28px, 3.5vw, 40px)', lineHeight: 1.15, marginBottom: '14px', letterSpacing: '-0.02em', color: '#0a192f' }}>
            What's Limiting Your Brand's Growth?
          </h2>
          <p style={{ fontSize: '17px', lineHeight: 1.6, maxWidth: '600px', margin: '0 auto', color: '#6b7280' }}>
            Brands lose control and momentum when distribution, pricing, and marketplace operations aren't aligned.
          </p>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4" style={{ gap: '20px' }}>
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <div
                key={card.testId}
                data-testid={card.testId}
                style={{
                  borderRadius: '16px',
                  padding: '32px 26px 34px',
                  backgroundColor: '#0f2137',
                  border: '1px solid rgba(127,211,197,0.12)',
                  boxShadow: '0 4px 24px rgba(10,25,47,0.2)',
                  transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                  cursor: 'default',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'translateY(-5px)';
                  e.currentTarget.style.boxShadow = '0 14px 44px rgba(10,25,47,0.35), 0 0 16px rgba(127,211,197,0.06)';
                  e.currentTarget.style.borderColor = 'rgba(127,211,197,0.3)';
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'translateY(0)';
                  e.currentTarget.style.boxShadow = '0 4px 24px rgba(10,25,47,0.2)';
                  e.currentTarget.style.borderColor = 'rgba(127,211,197,0.1)';
                }}
              >
                <Icon style={{ width: '40px', height: '40px', color: '#7FD3C5', marginBottom: '16px' }} strokeWidth={1.75} />
                <h3 className="font-bold" style={{ fontSize: '18px', marginBottom: '10px', color: 'rgba(255,255,255,0.95)', lineHeight: 1.3 }}>{card.title}</h3>
                <p style={{ fontSize: '14px', lineHeight: 1.65, color: 'rgba(255,255,255,0.45)', margin: 0 }}>
                  {card.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export const Home = () => {
  const trustIndicators = [
    { icon: CalendarDays, label: '6+ Years', sublabel: 'Experience' },
    { icon: MapPin, label: 'U.S. Based', sublabel: 'Company' },
    { icon: ShieldCheck, label: 'Brand-Compliant', sublabel: 'Distribution' },
    { icon: BadgeCheck, label: 'Authorized', sublabel: 'Retail Partner' }
  ];

  const channels = [
    { name: 'Amazon', description: 'Professional seller account with track record' },
    { name: 'Walmart Marketplace', description: 'Approved merchant with compliance history' },
    { name: 'eBay', description: 'Established store with positive feedback' },
    { name: 'B2B Partnerships', description: 'Direct wholesale and distribution relationships' }
  ];

  const values = [
    {
      icon: Shield,
      title: 'Brand Protection',
      description: 'We represent your products responsibly across marketplaces, protecting brand reputation and maintaining pricing integrity.'
    },
    {
      icon: CheckCircle,
      title: 'MAP Compliance',
      description: 'Strict adherence to manufacturer MAP policies with zero tolerance for pricing violations or unauthorized discounting.'
    },
    {
      icon: Package,
      title: 'Professional Fulfillment',
      description: 'Reliable storage, quality packaging, and efficient shipping operations supporting consistent marketplace fulfillment.'
    },
    {
      icon: TrendingUp,
      title: 'Marketplace Growth',
      description: 'Strategic inventory planning, optimized listings, and managed marketplace advertising designed to increase product visibility and support long-term brand growth.'
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative bg-navy-950 text-white overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/hero-warehouse-new.png"
            alt="Warehouse manager with tablet"
            className="w-full h-full object-cover"
            style={{ objectPosition: 'center center' }}
          />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(to right, rgba(10,25,47,0.88) 0%, rgba(10,25,47,0.7) 40%, rgba(10,25,47,0.45) 70%, rgba(10,25,47,0.3) 100%)' }}></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6 py-32 md:py-44">
          <div style={{ maxWidth: '600px' }}>
            <h1
              className="text-white animate-fade-in"
              style={{
                fontFamily: "'General Sans', sans-serif",
                fontSize: 'clamp(44px, 5.5vw, 72px)',
                lineHeight: 1.08,
                fontWeight: 700,
                letterSpacing: '-0.025em',
              }}
            >
              Scale Your Brand Across Major Marketplaces
            </h1>
            <p
              className="text-gray-300 animate-fade-in"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '18px',
                lineHeight: 1.7,
                opacity: 0.9,
                maxWidth: '540px',
                marginTop: '28px',
                marginBottom: '36px',
                animationDelay: '0.15s',
              }}
            >
              Strategic distribution, marketplace expertise, and performance-driven growth across leading e-commerce marketplaces.
            </p>
            <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 animate-fade-in" style={{ animationDelay: '0.3s' }}>
              <Link to="/partner">
                <Button size="lg" className="text-white font-semibold w-full sm:w-auto shadow-lg hover:shadow-xl transition-all px-8 py-3 text-base group" style={{ borderRadius: '10px', backgroundColor: '#2A8A6E' }}
                  onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#237a60'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
                  onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#2A8A6E'; e.currentTarget.style.transform = 'translateY(0)'; }}
                >
                  Scale With Us <span style={{ display: 'inline-block', marginLeft: '4px', transition: 'transform 0.25s ease' }} className="group-hover:translate-x-0.5">↗</span>
                </Button>
              </Link>
              <Link to="/about">
                <Button size="lg" variant="outline" className="border-white/40 text-white hover:bg-white/10 w-full sm:w-auto backdrop-blur-sm px-8 py-3 text-base" style={{ borderRadius: '10px' }}>
                  Learn More
                </Button>
              </Link>
            </div>
            <p
              className="text-gray-400 animate-fade-in"
              style={{
                fontFamily: "'Inter', sans-serif",
                fontSize: '13px',
                letterSpacing: '0.04em',
                marginTop: '28px',
                animationDelay: '0.45s',
              }}
            >
              Trusted by brands scaling across major e-commerce platforms
            </p>
          </div>
        </div>
      </section>

      {/* Credibility Strip */}
      <section className="py-8 bg-navy-900" data-testid="hero-credibility-strip">
        <div className="max-w-6xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 text-center" style={{ gap: 0 }}>
            {[
              { value: '$40M+', label: 'Marketplace Revenue' },
              { value: '6+ Years', label: 'Experience' },
              { value: 'Brand-Compliant', label: 'Distribution' },
              { value: 'U.S.-Based', label: 'Operations' },
            ].map((stat, i) => (
              <div
                key={stat.label}
                className="flex flex-col items-center justify-center"
                style={{
                  padding: '16px 20px',
                  borderRight: i < 3 ? '1px solid rgba(255,255,255,0.12)' : 'none',
                }}
              >
                <div
                  className="font-bold text-white"
                  style={{
                    fontSize: 'clamp(22px, 2.5vw, 32px)',
                    lineHeight: 1.2,
                    whiteSpace: 'nowrap',
                    marginBottom: '6px',
                  }}
                >
                  {stat.value}
                </div>
                <div
                  className="text-teal-400 font-medium"
                  style={{ fontSize: '13px', letterSpacing: '0.04em' }}
                >
                  {stat.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pain Points Section */}
      <PainPointsSection />

      {/* Built for Brand Protection */}
      <section className="py-24 bg-navy-950" data-testid="brand-protection-section">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-14">Built for Brand Protection</h2>
          <div className="grid md:grid-cols-3 gap-12">
            <div className="text-center">
              <ShieldCheck className="h-10 w-10 mx-auto mb-4" style={{ color: '#7FD3C5' }} strokeWidth={1.75} />
              <h3 className="text-xl font-bold text-white mb-3">MAP Compliance</h3>
              <p className="text-gray-400 leading-relaxed">
                Strict adherence to brand pricing policies and controlled marketplace distribution.
              </p>
            </div>
            <div className="text-center">
              <Boxes className="h-10 w-10 mx-auto mb-4" style={{ color: '#7FD3C5' }} strokeWidth={1.75} />
              <h3 className="text-xl font-bold text-white mb-3">Professional Fulfillment</h3>
              <p className="text-gray-400 leading-relaxed">
                Reliable storage, packaging, and logistics infrastructure supporting scalable distribution.
              </p>
            </div>
            <div className="text-center">
              <TrendingUp className="h-10 w-10 mx-auto mb-4" style={{ color: '#7FD3C5' }} strokeWidth={1.75} />
              <h3 className="text-xl font-bold text-white mb-3">Marketplace Expertise</h3>
              <p className="text-gray-400 leading-relaxed">
                Operational experience across leading marketplaces, combined with expert optimization, performance advertising, and strategic growth execution.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Sales Channels */}
      <section className="relative py-24 overflow-hidden" data-testid="sales-channels-section">
        <div className="absolute inset-0">
          <img
            src="/warehouse-team.png"
            alt="Warehouse fulfillment team"
            className="w-full h-full object-cover object-center md:object-center"
            style={{ objectPosition: '40% center' }}
          />
          <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/90 to-navy-950/70"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-6">
          <div className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white" style={{ marginBottom: '24px' }}>
              Multi-Channel Distribution
            </h2>
            <p className="text-lg text-gray-300 max-w-2xl mx-auto" style={{ marginBottom: '40px' }}>
              Established presence across major e-commerce platforms with professional management and compliance oversight.
            </p>
          </div>
          <div
            className="flex flex-wrap justify-center items-baseline"
            style={{ gap: '72px', marginTop: '52px' }}
            data-testid="platform-logos-row"
          >
            {['Amazon', 'Walmart', 'eBay', 'Shopify', 'B2B'].map((name) => (
              <span
                key={name}
                data-testid={`platform-${name.toLowerCase()}`}
                style={{
                  color: '#FFFFFF',
                  fontFamily: "'General Sans', 'Inter', sans-serif",
                  fontWeight: 600,
                  fontSize: '24px',
                  letterSpacing: '0.04em',
                  lineHeight: 1,
                  opacity: 0.75,
                  transition: 'opacity 0.25s ease',
                  cursor: 'default',
                }}
                onMouseEnter={e => { e.currentTarget.style.opacity = '1'; }}
                onMouseLeave={e => { e.currentTarget.style.opacity = '0.75'; }}
              >
                {name}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Why Brands Choose VERLINE */}
      <section className="py-24" style={{ backgroundColor: '#F7FAFC' }} data-testid="why-brands-section">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-navy-900 mb-4">
              Why Brands Choose VERLINE
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              VERLINE combines marketplace expertise, disciplined distribution, and advertising support to help brands expand responsibly across major e-commerce platforms.
            </p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4" style={{ gap: '20px' }}>
            {[
              { icon: Award, title: 'Proven Results', desc: 'We produce measurable outcomes through disciplined execution, ensuring pricing integrity, controlled expansion, and sustained marketplace performance.' },
              { icon: CheckCircle, title: 'MAP-Aligned Operations', desc: 'Our distribution approach is built to support compliant pricing, controlled channel expansion, and marketplace consistency.' },
              { icon: TrendingUp, title: 'Advertising & Listing Support', desc: 'Dedicated marketplace advertising specialists and optimized listing strategies help increase visibility and support sell-through.' },
              { icon: Package, title: 'Operational Reliability', desc: 'Professional fulfillment, inventory coordination, and multi-channel marketplace experience help brands scale with confidence.' },
            ].map((card) => {
              const Icon = card.icon;
              return (
                <div
                  key={card.title}
                  style={{
                    borderRadius: '16px',
                    padding: '32px 26px 34px',
                    backgroundColor: '#0f2137',
                    border: '1px solid rgba(127,211,197,0.12)',
                    boxShadow: '0 4px 24px rgba(10,25,47,0.2)',
                    transition: 'transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease',
                    cursor: 'default',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.transform = 'translateY(-5px)';
                    e.currentTarget.style.boxShadow = '0 14px 44px rgba(10,25,47,0.35), 0 0 16px rgba(127,211,197,0.06)';
                    e.currentTarget.style.borderColor = 'rgba(127,211,197,0.3)';
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.transform = 'translateY(0)';
                    e.currentTarget.style.boxShadow = '0 4px 24px rgba(10,25,47,0.2)';
                    e.currentTarget.style.borderColor = 'rgba(127,211,197,0.12)';
                  }}
                >
                  <Icon style={{ width: '40px', height: '40px', color: '#7FD3C5', marginBottom: '16px' }} strokeWidth={1.75} />
                  <h3 className="font-bold" style={{ fontSize: '18px', marginBottom: '10px', color: 'rgba(255,255,255,0.95)', lineHeight: 1.3 }}>{card.title}</h3>
                  <p style={{ fontSize: '14px', lineHeight: 1.65, color: 'rgba(255,255,255,0.45)', margin: 0 }}>
                    {card.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 text-white relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="/hero-analytics.png"
            alt="Analytics dashboard workspace"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0" style={{ background: 'rgba(10,25,47,0.64)' }}></div>
        </div>
        <div className="max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="text-3xl md:text-5xl font-bold mb-6">
            Let's Grow Your Brand Together
          </h2>
          <p className="text-lg text-gray-300 mb-10 leading-relaxed max-w-2xl mx-auto">
            We partner with brands and distributors seeking reliable marketplace distribution, professional operations, and scalable growth.
          </p>
          <Link to="/partner">
            <Button size="lg" className="text-white font-semibold shadow-xl hover:shadow-2xl transition-all px-10 py-4 text-base group" style={{ borderRadius: '10px', backgroundColor: '#2A8A6E' }}
              onMouseEnter={e => { e.currentTarget.style.backgroundColor = '#237a60'; e.currentTarget.style.transform = 'translateY(-1px)'; }}
              onMouseLeave={e => { e.currentTarget.style.backgroundColor = '#2A8A6E'; e.currentTarget.style.transform = 'translateY(0)'; }}
            >
              Scale With Us <span style={{ display: 'inline-block', marginLeft: '4px', transition: 'transform 0.25s ease' }} className="group-hover:translate-x-0.5">↗</span>
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};
